import React, { useRef, useState, useEffect, useCallback } from "react";
import { fileUrl } from "../../lib/api";

const A4_W = 210; // mm
const A4_H = 297; // mm

/**
 * Visual A4 editor. Renders draggable elements over a scaled A4 page.
 * Props:
 *  - settings: current settings object
 *  - onChange(patch): merge patch into settings
 *  - selected: currently selected element id
 *  - setSelected(id)
 */
const A4Editor = ({ settings, onChange, selected, setSelected }) => {
  const pageRef = useRef(null);
  const [dragging, setDragging] = useState(null); // {id, startX, startY, origX, origY}
  const [scale, setScale] = useState(2.2); // px per mm

  // Fit-to-container scale calculation
  useEffect(() => {
    const compute = () => {
      const el = pageRef.current?.parentElement;
      if (!el) return;
      const containerW = el.clientWidth - 24;
      const maxScale = Math.min((el.clientHeight - 40) / A4_H, containerW / A4_W);
      setScale(Math.max(1.4, Math.min(maxScale, 2.6)));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const pxW = A4_W * scale;
  const pxH = A4_H * scale;

  const onMouseDown = useCallback((id, xMm, yMm) => (e) => {
    e.stopPropagation();
    setSelected(id);
    setDragging({ id, startX: e.clientX, startY: e.clientY, origX: xMm, origY: yMm });
  }, [setSelected]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = (e.clientX - dragging.startX) / scale;
      const dy = (e.clientY - dragging.startY) / scale;
      const newX = Math.max(0, Math.min(A4_W, dragging.origX + dx));
      const newY = Math.max(0, Math.min(A4_H, dragging.origY + dy));
      const patch = {};
      const map = {
        title:  ["title_x_mm", "title_y_mm"],
        header: ["header_x_mm", "header_y_mm"],
        agency: ["agency_x_mm", "agency_y_mm"],
        logo:   ["logo_x_mm", "logo_y_mm"],
      };
      if (dragging.id === "footer") {
        // y counts from bottom
        patch["footer_x_mm"] = Math.round(newX * 10) / 10;
        patch["footer_y_mm_from_bottom"] = Math.max(0, Math.round((A4_H - newY) * 10) / 10);
      } else if (map[dragging.id]) {
        patch[map[dragging.id][0]] = Math.round(newX * 10) / 10;
        patch[map[dragging.id][1]] = Math.round(newY * 10) / 10;
      }
      onChange(patch);
    };
    const onUp = () => setDragging(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, scale, onChange]);

  const s = settings;
  const alignStyle = (a) => ({ left: "flex-start", center: "center", right: "flex-end" }[a] || "flex-start");
  const textAlignFor = (a) => a || "left";
  const fontMap = {
    "Helvetica": "'Plus Jakarta Sans', Helvetica, Arial, sans-serif",
    "Times-Roman": "'Fraunces', 'Times New Roman', Times, serif",
    "Courier": "'JetBrains Mono', Courier, monospace",
  };
  const baseFont = fontMap[s.font_family] || fontMap["Helvetica"];

  const Element = ({ id, x, y, children, style: st = {}, anchorFromBottom = false, w, align }) => {
    const active = selected === id;
    const isDrag = dragging?.id === id;
    // Anchor: left = px = x*scale. For agency (align right/center), the box grows from x point.
    const boxStyle = {
      position: "absolute",
      left: x * scale,
      top: anchorFromBottom ? undefined : y * scale,
      bottom: anchorFromBottom ? y * scale : undefined,
      transform: (() => {
        if (align === "center") return "translate(-50%, 0)";
        if (align === "right") return "translate(-100%, 0)";
        return "none";
      })(),
      width: w ? w * scale : undefined,
      cursor: isDrag ? "grabbing" : "grab",
      outline: active ? "2px solid #0284C7" : "1px dashed rgba(2,132,199,0.25)",
      outlineOffset: 2,
      background: active ? "rgba(2,132,199,0.06)" : "transparent",
      borderRadius: 4,
      padding: 2,
      userSelect: "none",
      whiteSpace: "pre-wrap",
      fontFamily: baseFont,
      ...st,
    };
    return (
      <div
        role="button"
        data-testid={`editor-el-${id}`}
        onMouseDown={onMouseDown(id, x, anchorFromBottom ? A4_H - y : y)}
        onClick={(e) => { e.stopPropagation(); setSelected(id); }}
        style={boxStyle}
      >
        {children}
      </div>
    );
  };

  return (
    <div className="w-full h-full flex items-center justify-center overflow-auto p-3 bg-slate-100 rounded-2xl">
      <div
        ref={pageRef}
        onClick={() => setSelected(null)}
        style={{
          width: pxW,
          height: pxH,
          position: "relative",
          background: s.template_path ? "#fff" : (s.page_bg_color || "#fff"),
          boxShadow: "0 20px 60px -20px rgba(15,23,42,0.35), 0 2px 6px rgba(15,23,42,0.12)",
          overflow: "hidden",
          borderRadius: 3,
        }}
      >
        {/* Template background */}
        {s.template_path && (
          <img
            src={fileUrl(s.template_path)}
            alt="plantilla"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
          />
        )}

        {/* Top accent bar (only when no template) */}
        {!s.template_path && (
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0,
            height: 4 * scale, background: s.accent_color,
          }} />
        )}

        {/* Logo */}
        {s.show_logo && s.logo_path && (
          <Element id="logo" x={s.logo_x_mm} y={s.logo_y_mm} w={s.logo_width_mm}>
            <img
              src={fileUrl(s.logo_path)}
              alt="logo"
              draggable={false}
              style={{ width: "100%", height: "auto", display: "block", pointerEvents: "none" }}
            />
          </Element>
        )}

        {/* Agency name */}
        {s.show_agency_name && s.agency_name && (
          <Element id="agency" x={s.agency_x_mm} y={s.agency_y_mm} align={s.agency_align}>
            <div style={{
              color: s.text_color,
              fontWeight: 700,
              fontSize: (s.agency_font_size || 11) * scale * 0.35,
              textAlign: textAlignFor(s.agency_align),
              whiteSpace: "nowrap",
            }}>
              {s.agency_name}
            </div>
          </Element>
        )}

        {/* Header (multi-line) */}
        {s.header_text && (
          <Element id="header" x={s.header_x_mm} y={s.header_y_mm} align={s.header_align}>
            <div style={{
              color: s.muted_color,
              fontSize: (s.header_font_size || 9) * scale * 0.35,
              textAlign: textAlignFor(s.header_align),
              lineHeight: 1.25,
            }}>
              {s.header_text}
            </div>
          </Element>
        )}

        {/* Title */}
        <Element id="title" x={s.title_x_mm} y={s.title_y_mm} align={s.title_align}>
          <div style={{
            color: s.text_color,
            fontWeight: 700,
            fontSize: (s.title_font_size || 22) * scale * 0.35,
            textAlign: textAlignFor(s.title_align),
            lineHeight: 1.1,
            whiteSpace: "nowrap",
          }}>
            {s.title_text || "Cotización de Viaje"}
          </div>
        </Element>

        {/* Body preview (static — not draggable) */}
        <div style={{
          position: "absolute",
          left: 15 * scale,
          top: ((s.title_y_mm || 45) + 15) * scale,
          right: 15 * scale,
          pointerEvents: "none",
          fontFamily: baseFont,
          fontSize: 4 * scale,
          color: s.text_color,
        }}>
          {/* Passenger card */}
          <div style={{ background: s.card_bg_color, borderRadius: 6, padding: 4 * scale, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 * scale }}>
            <div>
              <div style={{ color: s.accent_color, fontSize: 2.4 * scale, fontWeight: 700, letterSpacing: 1 }}>{s.passenger_label || "PASAJERO"}</div>
              <div style={{ color: s.text_color, fontSize: 4.5 * scale, fontWeight: 700, marginTop: 1 * scale }}>María González</div>
            </div>
            <div>
              <div style={{ color: s.accent_color, fontSize: 2.4 * scale, fontWeight: 700, letterSpacing: 1 }}>{s.destination_label || "DESTINO"}</div>
              <div style={{ color: s.text_color, fontSize: 4.5 * scale, fontWeight: 700, marginTop: 1 * scale }}>Cancún</div>
            </div>
            <div>
              <div style={{ color: s.accent_color, fontSize: 2.4 * scale, fontWeight: 700, letterSpacing: 1 }}>{s.passengers_label || "PASAJEROS"}</div>
              <div style={{ color: s.text_color, fontSize: 4.5 * scale, fontWeight: 700, marginTop: 1 * scale }}>2</div>
            </div>
          </div>

          {/* Flights section */}
          <div style={{ marginTop: 5 * scale }}>
            <div style={{ color: s.accent_color, fontSize: 2.4 * scale, fontWeight: 700, letterSpacing: 1 }}>{s.flights_overline || "ITINERARIO DE VUELOS"}</div>
            <div style={{ color: s.text_color, fontSize: 5 * scale, fontWeight: 700, margin: `${1 * scale}px 0` }}>{s.flights_title || "Vuelos"}</div>
            <div style={{ background: s.dark_bg_color, color: "#fff", padding: `${1.6 * scale}px ${2 * scale}px`, borderRadius: 3, fontSize: 3.2 * scale, display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr 1fr 1.4fr", gap: 2 }}>
              <span>Fecha</span><span>Origen</span><span>Destino</span><span>Salida</span><span>Llegada</span><span>Equipaje</span>
            </div>
            <div style={{ padding: `${1.6 * scale}px ${2 * scale}px`, fontSize: 3.2 * scale, display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr 1fr 1.4fr", gap: 2, borderBottom: `1px solid #E2E8F0` }}>
              <span>10/04</span><span>EZE</span><span>CUN</span><span>22:15</span><span>05:40</span><span>Bodega 23kg</span>
            </div>
            <div style={{ padding: `${1 * scale}px ${2 * scale}px`, color: s.price_color, fontWeight: 700, fontSize: 3.2 * scale }}>
              {s.flight_price_pp_label || "Precio por persona"}: US$ 850 · {s.flight_price_total_label || "Precio total"}: US$ 1,700
            </div>
          </div>

          {/* Hotels section */}
          <div style={{ marginTop: 4 * scale }}>
            <div style={{ color: s.accent_color, fontSize: 2.4 * scale, fontWeight: 700, letterSpacing: 1 }}>{s.hotels_overline || "ALOJAMIENTOS"}</div>
            <div style={{ color: s.text_color, fontSize: 5 * scale, fontWeight: 700, margin: `${1 * scale}px 0` }}>{s.hotels_title || "Hoteles"}</div>
            <div style={{ display: "grid", gridTemplateColumns: "38% 1fr", gap: 2 * scale, border: `1px solid #E2E8F0`, borderRadius: 4, padding: 2 * scale, background: "#fff" }}>
              <div style={{ background: s.card_bg_color, borderRadius: 3, height: 22 * scale, display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: 3 * scale }}>Imagen</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 4.5 * scale, color: s.text_color }}>Hotel Riviera Maya</div>
                <div style={{ color: s.accent_color, fontSize: 2.4 * scale, fontWeight: 700, letterSpacing: 1, marginTop: 0.6 * scale }}>7 NOCHES · DOBLE SUPERIOR</div>
                <div style={{ color: s.muted_color, fontSize: 3 * scale, marginTop: 0.6 * scale }}>Régimen: All Inclusive</div>
                <div style={{ color: s.text_color, fontSize: 3 * scale, marginTop: 0.6 * scale }}>A pie de playa, piscina y spa.</div>
                <div style={{ color: s.price_color, fontWeight: 700, fontSize: 3.4 * scale, marginTop: 1 * scale }}>
                  {s.hotel_price_pp_label || "Precio por persona"}: US$ 1,200<br/>
                  {s.hotel_price_total_label || "Precio total"}: US$ 2,400
                </div>
              </div>
            </div>
          </div>

          {/* Total bar (if show_total in preview) */}
          {s.show_total_preview !== false && (
            <div style={{
              marginTop: 4 * scale,
              background: s.dark_bg_color, color: "#fff", borderRadius: 4,
              padding: 3 * scale, display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ color: "#94A3B8", fontSize: 2.4 * scale, letterSpacing: 1, fontWeight: 700 }}>{s.total_label || "TOTAL COTIZACIÓN"}</span>
              <span style={{ color: s.price_color, fontSize: 6 * scale, fontWeight: 700 }}>US$ 4,100.00</span>
            </div>
          )}

          {/* Notes */}
          <div style={{ marginTop: 4 * scale }}>
            <div style={{ color: s.accent_color, fontSize: 2.4 * scale, fontWeight: 700, letterSpacing: 1 }}>{s.notes_overline || "NOTAS"}</div>
            <div style={{ color: s.text_color, fontSize: 3.4 * scale, marginTop: 0.8 * scale }}>Traslados aeropuerto-hotel incluidos. Impuestos y tasas incluidos.</div>
          </div>
        </div>

        {/* Footer (anchored from bottom) */}
        {s.footer_text && (
          <Element
            id="footer"
            x={s.footer_x_mm}
            y={s.footer_y_mm_from_bottom}
            anchorFromBottom
            align={s.footer_align}
          >
            <div style={{
              color: "#94A3B8",
              fontStyle: "italic",
              fontSize: (s.footer_font_size || 9) * scale * 0.35,
              textAlign: textAlignFor(s.footer_align),
              lineHeight: 1.25,
            }}>
              {s.footer_text}
            </div>
          </Element>
        )}

        {/* Rulers */}
        <div style={{
          position: "absolute", top: 0, left: 0, bottom: 0, right: 0,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(to right, rgba(148,163,184,0.12) 1px, transparent 1px)," +
            "linear-gradient(to bottom, rgba(148,163,184,0.12) 1px, transparent 1px)",
          backgroundSize: `${10 * scale}px ${10 * scale}px`,
        }} />
      </div>
    </div>
  );
};

export default A4Editor;
